// API client for the Studio worker.
// All requests credentialed via the session cookie minted by the studio-api
// magic-link flow (cookie scoped to .dreamlink.global once cut over).

// Single source of truth for the Studio API origin. Env var wins; otherwise
// derive from the current host so studio.dreamlink.global -> studio-api.dreamlink.global
// and studio.imorbis.com -> studio-api.imorbis.com both resolve during the
// migration window.
export function apiBase(): string {
  const env = (import.meta.env.VITE_STUDIO_API_URL as string | undefined)?.trim();
  if (env) return env;
  if (typeof location !== 'undefined') {
    if (location.hostname === 'localhost') return 'http://localhost:8787';
    if (location.hostname.endsWith('dreamlink.global')) return 'https://studio-api.dreamlink.global';
  }
  return 'https://studio-api.imorbis.com';
}

const API_BASE = apiBase();

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function call<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401) {
    // Operators sign in by email magic-link on the Studio's own domain
    // (cookie scoped to .dreamlink.global). The old orbis-admin Google OAuth
    // bounce is retired.
    window.location.href = '/operator/login';
    throw new Error('redirecting to sign in');
  }

  if (!res.ok) {
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      /* noop */
    }
    throw new Error(`API ${res.status}: ${detail || res.statusText}`);
  }

  return res.json() as Promise<T>;
}

// multipart/form-data variant of `call`. Do NOT set Content-Type — the browser
// sets it (with the boundary) from the FormData body. Same auth + error
// conventions as `call`.
async function multipart<T>(path: string, body: FormData, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body,
    signal,
  });

  if (res.status === 401) {
    window.location.href = '/operator/login';
    throw new Error('redirecting to sign in');
  }

  if (!res.ok) {
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      /* noop */
    }
    throw new Error(`API ${res.status}: ${detail || res.statusText}`);
  }

  return res.json() as Promise<T>;
}

// ── Domain types ────────────────────────────────────────────────────────────
export interface Brand {
  id: string;
  tenant_id: string | null;
  name: string;
  slug: string | null;
  status: string;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Campaign {
  id: string;
  brand_id: string;
  brand_name: string | null;
  brand_slug: string | null;
  title: string;
  artist: string | null;
  track: string | null;
  status: string;
  vertical: string;
  budget_total: number;
  budget_spent: number;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
  geography?: string | null;
  cpm_rate?: number | null;
  brief?: string | null;
  clips_wanted?: number | null;
}

export interface CampaignVendor {
  id: string;
  vendor_slug: string;
  lane: string;
  budget_allocated: number;
  budget_spent: number;
  status: string;
  brief_sent_at: string | null;
  notes: string | null;
}

export interface ClipSummary {
  total: number;
  claimed: number;
  submitted: number;
  verified: number;
  rejected: number;
  total_views: number;
  total_earnings_cents: number;
}

export interface CampaignDetail {
  campaign: Campaign;
  vendors: CampaignVendor[];
  clip_summary: ClipSummary;
}

export type CampaignAssetKind = 'audio' | 'video' | 'image' | 'doc' | 'source';

export interface CampaignAsset {
  id: string;
  campaign_id: string;
  kind: CampaignAssetKind;
  filename: string;
  size_bytes: number;
  content_type: string | null;
  url?: string | null;
  created_at: string;
}

export interface Clip {
  id: string;
  campaign_id: string;
  campaign_title: string | null;
  campaign_artist: string | null;
  serial_code: string;
  title: string | null;
  status: string;
  claimed_by_contact_id: string;
  claimed_at: string | null;
  submitted_url: string | null;
  submitted_at: string | null;
  platform: string | null;
  verified_views: number;
  earnings_cents: number;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClipViewSnapshot {
  id: number;
  views: number;
  source: string;
  captured_at: string;
}

export interface ClipDetail {
  clip: Clip & { campaign_cpm_rate?: number };
  view_history: ClipViewSnapshot[];
}

export interface BrandInvite {
  id: string;
  label: string | null;
  company_name_hint: string;
  email_hint: string | null;
  generated_by: string;
  expires_at: string;
  consumed_at: string | null;
  brand_id: string | null;
  consumed_email: string | null;
  created_at: string;
}

export interface BrandInquiry {
  id: string;
  status: 'submitted' | 'contacted' | 'qualified' | 'unqualified' | 'account_created';
  company_name: string;
  website_or_social: string;
  contact_name: string;
  contact_email: string;
  what_they_want: string;
  budget_range: string;
  service?: 'localization' | 'clipping';
  operator_notes: string | null;
  last_contacted_at: string | null;
  qualified_at: string | null;
  unqualified_reason: string | null;
  account_created_at: string | null;
  brand_id: string | null;
  inquired_at: string;
  utm_source: string | null;
  utm_campaign: string | null;
}

// Derives the service type from a BrandInquiry. The API does not yet return a
// `service` column; localization inquiries are identified by the '[Localization]'
// prefix that public.ts prepends to what_they_want at write time. If a real
// `service` field is present on the object (future API upgrade), it takes
// precedence so this helper stays correct after that migration.
export function inquiryService(
  inq: Pick<BrandInquiry, 'what_they_want' | 'service'>
): 'localization' | 'clipping' {
  if (inq.service === 'localization' || inq.service === 'clipping') return inq.service;
  return typeof inq.what_they_want === 'string' &&
    inq.what_they_want.startsWith('[Localization]')
    ? 'localization'
    : 'clipping';
}

export interface DashboardData {
  campaigns: { active: number; draft: number; completed: number };
  clips: {
    total: number;
    claimed: number;
    submitted: number;
    verified: number;
    total_views: number;
  };
  payouts: { pending_count: number; pending_cents: number };
  creators: { active_30d: number };
  spend: { total_budget_cents: number; total_spent_cents: number };
  activity: Array<{
    kind: 'campaign' | 'clip';
    id: string;
    subject: string;
    status: string;
    brand: string | null;
    ts: string;
    extra_views?: number;
  }>;
  top_campaigns: Array<{
    id: string;
    title: string;
    artist: string | null;
    status: string;
    brand_name: string | null;
    total_views: number;
    clip_count: number;
  }>;
}

// ── Endpoints ───────────────────────────────────────────────────────────────
export const api = {
  health: () => call<{ ok: boolean }>('/health'),
  me: () =>
    call<{ authenticated: boolean; contact_id?: string; role?: string; email?: string }>(
      '/auth/me'
    ),

  brands: {
    list: () => call<{ brands: Brand[] }>('/api/brands'),
    get: (id: string) =>
      call<{ brand: Brand; campaign_summary: { total: number; active: number; budget_total_sum: number; budget_spent_sum: number } }>(
        `/api/brands/${id}`
      ),
  },

  campaigns: {
    list: (params?: { brand_id?: string; status?: string; vertical?: string }) => {
      const q = params
        ? new URLSearchParams(params as Record<string, string>).toString()
        : '';
      return call<{ campaigns: Campaign[] }>(`/api/campaigns${q ? `?${q}` : ''}`);
    },
    get: (id: string) => call<CampaignDetail>(`/api/campaigns/${id}`),
    create: (body: {
      brand_id: string;
      title: string;
      artist?: string;
      track?: string;
      brief?: string;
      budget_total?: number;
      vertical?: string;
      geography?: string;
      starts_at?: string;
      ends_at?: string;
    }) => call<{ ok: boolean; id: string }>('/api/campaigns', { method: 'POST', body }),
    update: (id: string, body: { status?: string; cpm_rate?: number; budget_total?: number; brief?: string }) =>
      call<{ ok: boolean; id: string }>(`/api/campaigns/${id}`, { method: 'PATCH', body }),
    // Operator approve + issue deposit invoice. Charges the card on file if the
    // brand saved one, else emails them a pay link. Returns which path ran.
    approve: (id: string, body?: { cpm_rate?: number; budget_total?: number }) =>
      call<{ ok: boolean; id: string; method: 'charged' | 'invoiced'; deposit_due_cents: number; status: string }>(
        `/api/campaigns/${id}/approve`,
        { method: 'POST', body: body ?? {} }
      ),
    // Attach a media file to a campaign. multipart/form-data with `file` + `kind`
    // (audio | video | image | doc | source). Worker returns { ok, asset }.
    // Uses a bespoke multipart fetch (the JSON `call` helper sets a JSON
    // Content-Type which would corrupt the multipart boundary) but keeps the
    // same conventions: API_BASE, credentials:'include', 401 → sign-in, error
    // text surfaced.
    uploadAsset: (campaignId: string, file: File, kind: CampaignAssetKind | string) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      return multipart<{ ok: boolean; asset: CampaignAsset }>(
        `/api/campaigns/${campaignId}/assets`,
        fd
      );
    },
    assets: (campaignId: string) =>
      call<{ assets: CampaignAsset[] }>(`/api/campaigns/${campaignId}/assets`),
  },

  clips: {
    list: (params?: { campaign_id?: string; status?: string; creator_id?: string; limit?: number }) => {
      const q = params
        ? new URLSearchParams(
            Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
              if (v !== undefined && v !== null) acc[k] = String(v);
              return acc;
            }, {})
          ).toString()
        : '';
      return call<{ clips: Clip[] }>(`/api/clips${q ? `?${q}` : ''}`);
    },
    get: (id: string) => call<ClipDetail>(`/api/clips/${id}`),
    claim: (body: { campaign_id: string; bounty_id?: string; contact_id?: string; title?: string }) =>
      call<{ ok: boolean; id: string; serial_code: string }>('/api/clips/claim', { method: 'POST', body }),
    submit: (id: string, body: { url: string; title?: string }) =>
      call<{ ok: boolean; id: string; platform: string | null }>(`/api/clips/${id}/submit`, { method: 'POST', body }),
    verify: (id: string, body: { views?: number; approve?: boolean; rejection_reason?: string }) =>
      call<{ ok: boolean; id: string; status: string; verified_views?: number; earnings_cents?: number }>(
        `/api/clips/${id}/verify`,
        { method: 'POST', body }
      ),
    appendViews: (id: string, body: { views: number; source?: string }) =>
      call<{ ok: boolean; id: string; views: number }>(`/api/clips/${id}/views`, { method: 'POST', body }),
  },

  bounties: {
    active: () => call<{ bounties: unknown[] }>('/api/bounties/active'),
  },

  payouts: {
    list: () => call<{ payouts: unknown[] }>('/api/payouts'),
    leaderboard: () =>
      call<{ leaderboard: Array<{ name: string; total_earnings: number; total_clips: number }> }>(
        '/api/payouts/leaderboard'
      ),
  },

  performance: {
    dashboard: () => call<DashboardData>('/api/performance/dashboard'),
    campaign: (id: string) => call(`/api/performance/campaign/${id}`),
  },
};

// ── Public endpoints (no auth) ──────────────────────────────────────────────
async function publicCall<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`);
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json() as Promise<T>;
}

async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    let detail = ''; try { detail = await r.text(); } catch { /* noop */ }
    throw new Error(`API ${r.status}: ${detail}`);
  }
  return r.json() as Promise<T>;
}

export interface PublicCampaign {
  id: string;
  public_slug: string;
  title: string;
  public_headline: string | null;
  hero_image_key: string | null;
  brand_name: string;
  total_verified_views: number;
}

export interface FeaturedClip {
  id: string;
  platform: string;
  url: string;
  embed_id: string;
  handle: string;
  caption: string | null;
}

export const publicApi = {
  campaigns: (featured = true) =>
    publicCall<{ campaigns: PublicCampaign[] }>(
      `/api/public/campaigns?featured=${featured ? 1 : 0}`
    ),
  campaign: (slug: string) =>
    publicCall<{ campaign: Record<string, unknown>; clips: Record<string, unknown>[] }>(
      `/api/public/campaigns/${slug}`
    ),
  featuredClips: () =>
    publicCall<{ clips: FeaturedClip[] }>(`/api/public/featured-clips`),
  brandInquiry: (body: { name: string; email: string; company?: string; budget_range?: string; message?: string; turnstileToken: string }) =>
    publicPost<{ ok: boolean; id: string; notified: boolean }>(`/api/public/brand-inquiry`, body),
};

// ── Public qual state shape ──────────────────────────────────────────────────
export interface QualState {
  state: string;
  completeness: number;
  economics_pref: string | null;
  distribution_pref: string | null;
  assets_readiness: string | null;
  analytics_method: string | null;
  analytics_granted_at: string | null;
  channel_url: string | null;
  content_notes: string | null;
  assets: { id: string; content_type: string; size_bytes: number }[];
}

// Public, token-gated localization qualification client. No session cookie;
// the token in the URL is the credential. Turnstile token sent with every write.
export const qualApi = {
  get: (token: string) =>
    fetch(`${apiBase()}/api/public/qual/${encodeURIComponent(token)}`).then(async (r) => {
      if (!r.ok) throw new Error(`qual_${r.status}`);
      return r.json() as Promise<QualState>;
    }),
  bucket: (token: string, bucket: string, value: unknown, turnstileToken: string | null) =>
    fetch(`${apiBase()}/api/public/qual/${encodeURIComponent(token)}/bucket`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bucket, value, turnstileToken }),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`bucket_${r.status}`);
      return r.json() as Promise<{ state: string; completeness: number }>;
    }),
  grant: (token: string, turnstileToken: string | null) =>
    fetch(`${apiBase()}/api/public/qual/${encodeURIComponent(token)}/grant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ asserted: true, turnstileToken }),
    }).then(async (r) => {
      if (!r.ok) throw new Error(`grant_${r.status}`);
      return r.json() as Promise<{ state: string; completeness: number; analytics_granted_at: string }>;
    }),
  upload: (token: string, file: File, turnstileToken: string | null) => {
    const fd = new FormData();
    fd.append('file', file);
    if (turnstileToken) fd.append('turnstileToken', turnstileToken);
    return fetch(`${apiBase()}/api/public/qual/${encodeURIComponent(token)}/upload`, {
      method: 'POST',
      body: fd,
    }).then(async (r) => {
      if (!r.ok) throw new Error(`upload_${r.status}`);
      return r.json() as Promise<{ asset: { id: string }; completeness: number; state: string }>;
    });
  },
};

// ── Brand portal (authenticated as role=brand via magic-link cookie) ────────
// Uses the same `call` helper which redirects to admin OAuth on 401 — we
// override that here because brand users sign in via /login (magic link),
// not admin Google OAuth.
async function brandCall<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('redirecting to sign in');
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw Object.assign(new Error(`API ${res.status}`), { status: res.status, detail });
  }
  return res.json() as Promise<T>;
}

export async function authMe() {
  const r = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
  if (!r.ok) return null;
  return (await r.json()) as {
    authenticated: true;
    role: string;
    email?: string;
    contact_id?: string;
    brand_id?: string;
    review_status?: string;
  };
}

// ── Operator admin endpoints ────────────────────────────────────────────────
export interface AdminDashboardStats {
  counts: {
    pending_brands: number;
    draft_campaigns: number;
    active_campaigns: number;
    submitted_clips_pending_brand: number;
  };
  drafts: Array<{
    id: string;
    title: string;
    brand_id: string;
    brand_name: string;
    budget_total: number;
    created_at: string;
  }>;
}

export interface AuditEntry {
  id: number;
  actor_contact_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  metadata: string | null; // JSON string
  created_at: string;
}

export const adminApi = {
  dashboardStats: () => call<AdminDashboardStats>('/api/admin/dashboard-stats'),
  verificationQueue: () =>
    call<{
      anomalies: Array<{
        id: string;
        clip_id: string;
        serial_code: string;
        platform: string;
        submitted_url: string;
        campaign_id: string;
        campaign_title: string;
        delta_views: number;
        delta_window_minutes: number;
        rate_per_minute: number;
        baseline_rate: number;
        z_score: number;
        reason: string;
        created_at: string;
      }>;
      dlq: Array<{
        id: string;
        source: string;
        attempt_count: number;
        max_attempts: number;
        last_error: string;
        next_attempt_at: string | null;
      }>;
      counts: Record<string, number>;
    }>('/api/admin/verification-queue'),
  resolveAnomaly: (id: string, resolution: 'approved' | 'rejected' | 'discounted', notes?: string) =>
    call<{ ok: boolean }>(`/api/admin/verification-queue/${id}`, {
      method: 'POST',
      body: { resolution, notes },
    }),
  agentRuns: (status?: string) => {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return call<{
      runs: Array<{
        id: string;
        dispatched_by: string | null;
        subagent_type: string;
        description: string;
        target_path: string | null;
        status: string;
        result_summary: string | null;
        result_severity: string | null;
        dispatched_at: string;
        finished_at: string | null;
        duration_ms: number | null;
        cost_cents: number | null;
      }>;
      counts: Record<string, number>;
    }>(`/api/admin/agent-runs${q}`);
  },
  auditLog: (params?: { action?: string; resource_type?: string; actor?: string; limit?: number }) => {
    const q = params
      ? new URLSearchParams(
          Object.entries(params).reduce<Record<string, string>>((a, [k, v]) => {
            if (v !== undefined && v !== null && v !== '') a[k] = String(v);
            return a;
          }, {})
        ).toString()
      : '';
    return call<{ entries: AuditEntry[] }>(`/api/admin/audit-log${q ? `?${q}` : ''}`);
  },
  brandReviewQueue: () =>
    call<{
      pending: Array<{ contact_id: string; email: string; status: string; requested_at: string }>;
      recent: Array<{ contact_id: string; email: string; status: string; reviewed_at: string }>;
    }>('/api/admin/brand-review-queue'),
  approveBrand: (email: string, brand_name?: string) =>
    call<{ ok: boolean; brand_id: string }>('/api/admin/brand-review/approve', {
      method: 'POST',
      body: { email, brand_name },
    }),
  rejectBrand: (email: string, notes?: string) =>
    call<{ ok: boolean }>('/api/admin/brand-review/reject', {
      method: 'POST',
      body: { email, notes },
    }),
  inquiries: (status: string = 'open') =>
    call<{ inquiries: BrandInquiry[] }>(
      `/api/admin/inquiries?status=${encodeURIComponent(status)}`
    ),
  updateInquiry: (
    id: string,
    patch: {
      status?: 'submitted' | 'contacted' | 'qualified' | 'unqualified';
      operator_notes?: string;
      unqualified_reason?: string;
      mark_contacted?: boolean;
    }
  ) =>
    call<{ ok: boolean }>(`/api/admin/inquiries/${id}`, {
      method: 'PATCH',
      body: patch,
    }),
  createAccountFromInquiry: (id: string, brand_name?: string) =>
    call<{ ok: boolean; brand_id: string; email: string; login_link: string; emailed: boolean }>(
      `/api/admin/inquiries/${id}/create-account`,
      { method: 'POST', body: { brand_name } }
    ),
  // ── Localization qualification (operator) ─────────────────────────────────
  inquiryQual: (id: string) =>
    call<{
      qual: Record<string, unknown>;
      assets: { id: string; content_type: string; size_bytes: number; created_at: string }[];
      analytics_unverified: boolean;
    }>(`/api/admin/inquiries/${id}/qual`),
  routeToPartner: (id: string) =>
    call<{ state: string; routed_at: string }>(
      `/api/admin/inquiries/${id}/route-to-partner`,
      { method: 'POST', body: { confirm: true } }
    ),
  patchQual: (id: string, state: 'unqualified' | 'on_hold' | 'partner_responded') =>
    call<{ ok: boolean; state: string }>(
      `/api/admin/inquiries/${id}/qual`,
      { method: 'PATCH', body: { state } }
    ),
  qualAssetUrl: (assetId: string) => `${apiBase()}/api/admin/qual-assets/${assetId}`,
  generateInvite: (body: { company_name: string; email_hint?: string; label?: string; ttl_days?: number }) =>
    call<{ id: string; url: string; expires_at: string }>(
      '/api/admin/brands/invite-link',
      { method: 'POST', body }
    ),
  listInvites: () =>
    call<{ invites: BrandInvite[] }>('/api/admin/brands/invite-link'),
  setCampaignStatus: (
    id: string,
    status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled',
    extra?: { cpm_rate?: number; budget_total?: number; brief?: string }
  ) =>
    call<{ ok: boolean; id: string }>(`/api/campaigns/${id}`, {
      method: 'PATCH',
      body: { status, ...extra },
    }),
};

// ── Clipper (creator) ───────────────────────────────────────────────────────
async function clipperCall<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) {
    window.location.href = '/clipper';
    throw new Error('redirecting to clipper sign in');
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw Object.assign(new Error(`API ${res.status}`), { status: res.status, detail });
  }
  return res.json() as Promise<T>;
}

export interface ClipperConnection {
  provider: string;
  external_id: string | null;
  expires_at: string | null;
  updated_at: string | null;
}

export interface Bounty {
  id: string;
  title: string;
  artist: string | null;
  vertical: string | null;
  cpm_rate: number | null;
  budget_total: number | null;
  budget_spent: number | null;
  starts_at: string | null;
  ends_at: string | null;
  source_video_url: string | null;
  brand_name: string | null;
}

export interface MyClip {
  id: string;
  campaign_id: string;
  serial_code: string;
  status: 'claimed' | 'submitted' | 'verified' | 'paid' | 'rejected';
  submitted_url: string | null;
  submitted_at: string | null;
  platform: string | null;
  verified_views: number | null;
  earnings_cents: number | null;
  rejection_reason: string | null;
  claimed_at: string;
  updated_at: string;
  campaign_title: string | null;
  campaign_artist: string | null;
  cpm_rate: number | null;
  brand_name: string | null;
}

export interface Earnings {
  total_verified_views: number;
  total_earned_cents: number;
  total_paid_cents: number;
  balance_cents: number;
  pending_clips: number;
  verified_clips: number;
  cashout_threshold_cents: number;
}

export const clipperPortalApi = {
  bounties: () => clipperCall<{ bounties: Bounty[] }>('/api/clipper/bounties'),
  claim: (campaignId: string) =>
    clipperCall<{ ok: boolean; id: string; serial_code: string; existing: boolean }>(
      `/api/clipper/bounties/${encodeURIComponent(campaignId)}/claim`,
      { method: 'POST', body: {} }
    ),
  clips: () => clipperCall<{ clips: MyClip[] }>('/api/clipper/clips'),
  submit: (clipId: string, url: string) =>
    clipperCall<{ ok: boolean; platform: string | null; status: string }>(
      `/api/clipper/clips/${encodeURIComponent(clipId)}/submit`,
      { method: 'POST', body: { url } }
    ),
  earnings: () => clipperCall<Earnings>('/api/clipper/earnings'),
};

export const clipperApi = {
  // Clipper-scoped identity. Hits /auth/clipper/me (resolves ONLY the clipper
  // cookie) instead of the privilege-ordered /auth/me, so a creator who is
  // also signed into the operator/brand side still resolves as a creator on
  // this portal. Fixes the 2026-05-21 magic-link login loop.
  me: () =>
    fetch(`${API_BASE}/auth/clipper/me`, { credentials: 'include' }).then(async (r) =>
      r.ok
        ? ((await r.json()) as {
            authenticated: true;
            role: string;
            email?: string;
            contact_id?: string;
          })
        : null
    ),
  requestMagicLink: (email: string, turnstileToken: string) =>
    fetch(`${API_BASE}/auth/clipper/magic-link/request`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken }),
    }).then(async (r) => {
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; debug_link?: string };
      return { status: r.status, ...body };
    }),
  connections: () => clipperCall<{ connections: ClipperConnection[] }>('/auth/oauth/connections'),
  disconnect: (provider: string) =>
    clipperCall<{ ok: boolean }>('/auth/oauth/disconnect', {
      method: 'POST',
      body: { provider },
    }),
  signout: () =>
    fetch(`${API_BASE}/auth/clipper/signout`, {
      method: 'POST',
      credentials: 'include',
    }).then((r) => r.ok),
};

export interface BrandProfile {
  id: string;
  name: string;
  website: string | null;
  welcome_kickoff_when: string | null;
  onboarded_at: string | null;
}

// ── Creator (self-serve loop) ───────────────────────────────────────────────
export interface CreatorCampaign {
  id: string; title: string; brief: string | null; source_video_url: string | null;
  cpm_rate: number | null; vertical: string | null; ends_at: string | null;
  budget_total: number; budget_spent: number; brand_name: string | null;
}
export interface CreatorClip {
  id: string; campaign_id: string; serial_code: string; status: string;
  platform: string | null; submitted_url: string | null; verified_views: number;
  earnings_cents: number; rejection_reason: string | null; campaign_title: string | null;
}
export interface CreatorEarnings {
  total_earned_cents: number; paid_cents: number; clips_verified: number; total_views: number;
}

export const creatorApi = {
  campaigns: () => clipperCall<{ campaigns: CreatorCampaign[] }>('/api/creator/campaigns'),
  clips: () => clipperCall<{ clips: CreatorClip[] }>('/api/creator/clips'),
  claim: (campaign_id: string) =>
    clipperCall<{ ok: boolean; id: string; serial_code: string }>('/api/creator/clips/claim', { method: 'POST', body: { campaign_id } }),
  submit: (clipId: string, url: string) =>
    clipperCall<{ ok: boolean; id: string; platform: string | null }>(`/api/creator/clips/${clipId}/submit`, { method: 'POST', body: { url } }),
  earnings: () => clipperCall<CreatorEarnings>('/api/creator/earnings'),
  connectStart: () => clipperCall<{ onboarding_url: string; expires_at: number }>('/api/creator/connect/start', { method: 'POST' }),
  connectStatus: () => clipperCall<{ connected: boolean; payouts_enabled: boolean; onboarding_status?: string }>('/api/creator/connect/status'),
};

export const brandApi = {
  // Brand-scoped identity. Hits /auth/brand/me (resolves ONLY the brand cookie)
  // instead of the privilege-ordered /auth/me, so a brand user who is also
  // signed into the operator side still resolves as a brand on this portal.
  // Twin of clipperApi.me() — fixes the same login-loop class on /app.
  me: () =>
    fetch(`${API_BASE}/auth/brand/me`, { credentials: 'include' }).then(async (r) =>
      r.ok
        ? ((await r.json()) as {
            authenticated: true;
            role: string;
            email?: string;
            contact_id?: string;
            brand_id?: string | null;
            review_status?: string;
          })
        : null
    ),
  campaigns: () =>
    brandCall<{
      campaigns: Record<string, unknown>[];
      review_status: string;
      profile: BrandProfile | null;
    }>('/api/brand/campaigns'),
  campaign: (id: string) =>
    brandCall<{
      campaign: Record<string, unknown>;
      clips: Record<string, unknown>[];
      views: Record<string, unknown>[];
    }>(`/api/brand/campaigns/${id}`),
  brief: (body: Record<string, unknown>) =>
    brandCall<{ id: string; deposit_due_cents: number; final_due_cents: number }>(
      '/api/brand/campaigns',
      { method: 'POST', body }
    ),
  decideClip: (campaignId: string, clipId: string, action: 'approve' | 'reject', reason?: string) =>
    brandCall<{ ok: boolean; brand_decision: string }>(
      `/api/brand/campaigns/${campaignId}`,
      { method: 'PATCH', body: { clip_id: clipId, action, reason } }
    ),
  checkout: (campaignId: string) =>
    brandCall<{ url: string; session_id: string }>('/api/stripe/checkout', {
      method: 'POST',
      body: { campaign_id: campaignId },
    }),
  updateProfile: (patch: { name?: string; website?: string }) =>
    brandCall<{ ok: boolean }>('/api/brand/profile', { method: 'PATCH', body: patch }),
  completeOnboarding: (body: { kickoff_when?: string }) =>
    brandCall<{ ok: boolean }>('/api/brand/onboarding/complete', { method: 'POST', body }),
  // Card on file. setupCardSession returns a hosted Stripe Checkout (setup
  // mode) URL to redirect to; paymentMethod reports the saved card, if any.
  setupCardSession: () =>
    brandCall<{ url: string }>('/api/stripe/brand/setup-session', { method: 'POST' }),
  paymentMethod: () =>
    brandCall<{ card: BrandCard | null }>('/api/stripe/brand/payment-method'),
};

export interface BrandCard {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

// ── Operator (magic-link) ───────────────────────────────────────────────────
// Operator/admin sign-in on the Studio's own domain (cookie .dreamlink.global),
// replacing the retired orbis-admin Google OAuth bounce. Mirrors clipperApi.
export const operatorApi = {
  me: () =>
    fetch(`${API_BASE}/auth/operator/me`, { credentials: 'include' }).then(async (r) =>
      r.ok
        ? ((await r.json()) as { authenticated: true; role: string; email?: string; contact_id?: string })
        : null
    ),
  requestMagicLink: (email: string, turnstileToken: string) =>
    fetch(`${API_BASE}/auth/operator/magic-link/request`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, turnstileToken }),
    }).then(async (r) => {
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      return { status: r.status, ...body };
    }),
  signout: () =>
    fetch(`${API_BASE}/auth/operator/signout`, { method: 'POST', credentials: 'include' }).then((r) => r.ok),
};
